import { ticketWords } from './ModifierSheet';
import { Signal } from '@verlo/shared/components/ui/signal';
import { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import { ScrollArea } from '@verlo/shared/components/ui/scroll-area';
import {
  Plus, Minus, Trash2, Edit3, CreditCard, Utensils, MessageSquare, Gift, Undo2, Tag, MoreHorizontal, DoorOpen,
} from 'lucide-react';
import { REASONS as MANAGER_ACTION_REASONS } from './ManagerActionDialog';
import { amendedCount, groupByCourse, lineProgress, PROGRESS, primaryVerb } from '../../lib/courses';
import { unsentValue } from '../../lib/sentLines';

// The order, as one list (FOH plan round two, F): every line the table has, grouped by
// course, each with a rail that says where it is — new (not yet sent), sent, cooking,
// ready, served — and its own total. The bottom of the panel offers exactly one thing:
// send what's new, or take the bill. Everything else is behind "More".

// One order pad for the till (FOH UX review, wave 2). `mode="table"` is the order page —
// lines group by course, the verb is Send / Bill / Close. `mode="bar"` is the Bar page —
// no table, no send: the verb is Take payment, `headerExtra` carries the customer's name,
// `onClearCart` a way to drop the lot. The old `BarCartPanel` was a second pad with its own
// empty state, totals block and button; it is gone.
const OrderPanel = ({
  cart, order, tickets = [], orderPayments = [], remainingBalance, subtotal, vatAmount, orderTax,
  processing, lastAdded, mode = 'table', headerExtra = null, onClearCart, readOnly = false, onRefund,
  onUpdateQuantity, onRemoveItem, onEditItem, onCreateOrder, onCheckout, onCloseTable,
  formatCurrency, discountAmount = 0, discountLabel = 'Discount', orderTotal,
  canAdjust = false, onManagerAction, onUndoAdjustment,
  // Promotions: `offersAvailable` — bill-level offers a server can tap right now (opens the
  // Offers dialog from More); `isOffer` — the discount on the order is a tapped offer, so
  // undoing it needs no manager.
  offersAvailable = 0, onOffers, isOffer = false,
  // Messages on the table (30 Sep 2026): the strip above the lines, and the way to add one.
  // `pendingMessages` are the ones typed before the order exists — they read the same on the
  // pad, and travel with the send.
  onMessage, onRemoveMessage, pendingMessages = [],
  // `fill`: the page has a fixed height on a till's screen (lg and up) and the panel fills it,
  // so only the lines scroll and the total and the verb are always on screen.
  fill = false,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);
  // Tap-to-open lines (4:3 tills, Oct 2026, Martin's call): a line is a line or two until it's
  // tapped, then it shows its buttons at 44px. One open at a time, by the line's key.
  const [openLine, setOpenLine] = useState(null);
  // A line that's gone (removed, or sent and given its id) closes rather than handing its
  // buttons to whichever line takes its place.
  useEffect(() => {
    if (openLine && !cart.some((l, i) => (l.line_id || `new-${i}`) === openLine)) setOpenLine(null);
  }, [cart, openLine]);
  const flashRef = useRef(null);

  const hasDiscount = discountAmount > 0;
  // What is on the order, plus what has been typed but not sent yet.
  const messages = [...(order?.messages || []), ...pendingMessages];
  const grossShown = order?.subtotal != null ? order.subtotal : subtotal;
  // What the line offers gave. The server's `subtotal` is already net of them, so the
  // bill reads Subtotal (before offers) / Offers / Discount / Total.
  const offersGiven = order?.promotion_amount || 0;
  const itemsGross = grossShown + offersGiven;
  const totalShown = orderTotal != null ? orderTotal : subtotal;
  // What the server charged once there's an order; the venue's rate on the cart before.
  const vatShown = orderTotal != null ? (orderTax ?? 0) : vatAmount;
  const paid = orderPayments.length > 0 && remainingBalance <= 0;
  // Once there is an order the server's total is what it has been sent; a second round on
  // the pad is on top of it. The total a server reads to a guest is everything on the pad.
  const notSent = readOnly || orderTotal == null ? 0 : unsentValue(cart);

  const newCount = readOnly ? 0 : cart.filter((l) => !l.line_id).reduce((n, l) => n + (Number(l.quantity) || 1), 0);
  const amended = readOnly ? 0 : amendedCount(cart, order?.items);
  const verb = primaryVerb({ newCount, hasOrder: Boolean(order?.id), remaining: remainingBalance || totalShown, paid, mode, amended });
  const groups = groupByCourse(cart);
  const covers = cart.reduce((n, l) => n + (Number(l.quantity) || 1), 0);

  // A tap-to-add bump: scroll the newest line into view and let it flash once.
  const [flashing, setFlashing] = useState(null);
  useEffect(() => {
    if (lastAdded == null) return undefined;
    setFlashing(lastAdded);
    const el = flashRef.current;
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const t = setTimeout(() => setFlashing(null), 700);
    return () => clearTimeout(t);
  }, [lastAdded, cart.length]);

  const runPrimary = () => {
    if (verb.key === 'send') onCreateOrder();
    else if (verb.key === 'bill' || verb.key === 'pay') onCheckout();
    else if (verb.key === 'close') onCloseTable?.();
  };

  return (
    <Card className={`flex flex-col lg:w-[340px] lg:shrink-0 xl:w-96 ${fill ? 'h-[calc(100vh-8rem)] lg:h-full' : 'h-[calc(100vh-8rem)] lg:sticky lg:top-8'}`}>
      <CardHeader className="border-b shrink-0 py-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span>Order</span>
          <span className="flex items-center gap-2 text-xs font-normal text-ink-soft">
            {covers} item{covers === 1 ? '' : 's'}
            {mode === 'table' && newCount > 0 && <Badge className="ml-2 bg-signal-warn-tint text-signal-warn hover:bg-signal-warn-tint">{newCount} new</Badge>}
            {mode === 'table' && newCount === 0 && amended > 0 && <Badge className="ml-2 bg-signal-warn-tint text-signal-warn hover:bg-signal-warn-tint">{amended} changed</Badge>}
            {onClearCart && cart.length > 0 && (
              <button type="button" className="ml-1 text-ink-faint hover:text-signal-stop" onClick={onClearCart} title="Clear the order" data-testid="clear-cart-btn">
                <Trash2 size={14} />
              </button>
            )}
          </span>
        </CardTitle>
        {headerExtra && <div className="pt-2">{headerExtra}</div>}
      </CardHeader>

      {messages.length > 0 && (
        <div className="shrink-0 border-b border-hairline bg-white px-3 py-2 space-y-1" data-testid="order-messages">
          {messages.map((m) => (
            <div key={m.id} className="flex items-start gap-2 text-[12px]" data-testid={`order-message-${m.kind}`}>
              <Signal tone={m.kind === 'allergy' ? 'stop' : m.kind === 'house' ? 'info' : 'warn'}>
                {m.kind === 'house' ? 'floor' : m.kind}
              </Signal>
              <span className={`min-w-0 flex-1 ${m.kind === 'allergy' ? 'font-semibold text-signal-stop' : 'text-ink-soft'}`}>{m.text}</span>
              {!readOnly && onRemoveMessage && (
                <button type="button" className="shrink-0 text-ink-faint hover:text-signal-stop"
                        onClick={() => onRemoveMessage(m)} title="Remove this message"
                        data-testid={`order-message-remove-${m.id}`}>
                  <Undo2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-3">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Utensils className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Nothing on the order yet</p>
              <p className="text-xs">{mode === 'bar' ? 'Tap a drink to add it' : 'Tap a dish to add it'}</p>
            </div>
          ) : groups.map((g) => (
            <div key={g.course} className="mb-3" data-testid={`course-${g.course}`}>
              {mode === 'table' && (
              <div className="flex items-center gap-2 px-1 mb-1">
                <span className="text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">{g.label}</span>
                <span className="text-[10.5px] text-ink-faint">{g.lines.reduce((n, x) => n + (Number(x.line.quantity) || 1), 0)}</span>
                <span className="flex-1 border-t border-hairline" />
              </div>
              )}
              <div className="space-y-1.5">
                {g.lines.map(({ line: item, index }) => {
                  const progress = readOnly ? 'served' : lineProgress(item, tickets);
                  const p = PROGRESS[progress];
                  const isNew = progress === 'new';
                  const lineTotal = item.comp ? 0 : (item.subtotal ?? item.price * item.quantity);
                  const key = item.line_id || `new-${index}`;
                  const canOpen = !readOnly && progress !== 'voided';
                  const open = canOpen && openLine === key;
                  const toggle = () => { if (canOpen) setOpenLine(open ? null : key); };
                  const adjustable = canAdjust && item.line_id && !item.comp && item.price_override == null;
                  // A button inside the line does its own thing, not open or close the line.
                  const own = (fn) => (e) => { e.stopPropagation(); fn(); };
                  return (
                    <div
                      key={key}
                      ref={lastAdded === index ? flashRef : null}
                      className={`flex rounded-lg border overflow-hidden ${open ? 'border-verlo bg-surface-2' : `bg-white ${isNew && mode === 'table' ? 'border-signal-warn-line' : 'border-hairline'}`} ${flashing === index ? 'ring-2 ring-signal-warn-line' : ''}`}
                      data-testid={`cart-item-${index}`}
                    >
                      {mode === 'table' && <div className={`w-1 shrink-0 ${p.rail}`} />}
                      <div className="flex-1 min-w-0">
                        <div
                          role={canOpen ? 'button' : undefined}
                          tabIndex={canOpen ? 0 : undefined}
                          aria-expanded={canOpen ? open : undefined}
                          onClick={toggle}
                          onKeyDown={(e) => { if (canOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); } }}
                          className={`flex items-start gap-2 px-3 py-2.5 ${canOpen ? 'cursor-pointer' : ''}`}
                          data-testid={`line-toggle-${index}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="font-mono text-[12px] text-ink-soft tabular-nums shrink-0">{item.quantity}×</span>
                              <p className={`text-[13.5px] font-medium truncate ${progress === 'voided' ? 'line-through text-ink-faint' : ''}`}>{item.name}</p>
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] min-w-0">
                              {mode === 'table' && <span className={`font-medium ${p.text}`}>{p.word}</span>}
                              {item.modifiers?.length > 0 && <span className="text-signal-warn truncate">· {ticketWords(item.modifiers).join(', ')}</span>}
                            </div>
                            {/* A meal's parts, under its name: one line on the bill, and
                                the three things somebody is making. */}
                            {item.components?.length > 0 && (
                              <div className="mt-0.5 grid gap-px" data-testid={`line-components-${index}`}>
                                {item.components.map((c) => (
                                  <div key={`${c.group}-${c.menu_item_id}`} className="flex items-center gap-1 pl-3 text-[11px] text-ink-soft min-w-0">
                                    <span className="text-ink-faint shrink-0">·</span>
                                    <span className="truncate">{(c.quantity || 1) > 1 ? `${c.quantity}× ${c.name}` : c.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {item.allergy_notes && (
                              <div className="mt-1 flex items-center gap-1.5 min-w-0"><Signal tone="stop">allergy</Signal><span className="truncate text-[11px] font-medium text-signal-stop">{item.allergy_notes}</span></div>
                            )}
                            {item.notes && (
                              <div className="mt-1 flex items-center gap-1 text-[11px] text-ink-soft"><MessageSquare size={10} /><span className="truncate">{item.notes}</span></div>
                            )}
                            {item.promotion?.name && !item.comp && (
                              <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-signal-ok" data-testid={`line-offer-${index}`}>
                                <Tag size={10} /><span className="truncate">{item.promotion.name}</span>
                              </div>
                            )}
                            {/* A comp or a set price is part of what the line is, so it shows
                                whether the line is open or not, with its undo. */}
                            {canAdjust && item.line_id && (item.comp || item.price_override != null) && (
                              <div className="mt-1 text-[11px]">
                                {item.comp ? (
                                  <span className="inline-flex items-center gap-1 rounded bg-signal-ok-tint text-signal-ok px-1.5 py-0.5">
                                    <Gift size={10} /> Comp · {MANAGER_ACTION_REASONS.comp[item.comp_reason] || item.comp_reason}
                                    <button type="button" className="ml-0.5" onClick={own(() => onUndoAdjustment('comp', item))} data-testid={`uncomp-${index}`}><Undo2 size={10} /></button>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 rounded bg-signal-warn-tint text-signal-warn px-1.5 py-0.5">
                                    <Tag size={10} /> Price set · {MANAGER_ACTION_REASONS.price[item.price_override_reason] || item.price_override_reason}
                                    <button type="button" className="ml-0.5" onClick={own(() => onUndoAdjustment('price', item))} data-testid={`clear-override-${index}`}><Undo2 size={10} /></button>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className={`font-mono text-[13px] font-semibold tabular-nums ${item.comp ? 'text-signal-ok' : 'text-ink'}`}>
                              {item.promotion?.amount_off > 0 && !item.comp && (
                                <s className="mr-1.5 font-normal text-ink-faint">{formatCurrency(lineTotal + item.promotion.amount_off)}</s>
                              )}
                              {formatCurrency(lineTotal)}
                            </span>
                            <span className="text-[10px] text-ink-faint tabular-nums">{formatCurrency(item.price)} each</span>
                          </div>
                        </div>
                        {open && (
                          <div className="grid grid-cols-3 gap-1.5 px-3 pb-3" data-testid={`line-actions-${index}`}>
                            <div className="col-span-2 flex h-11 items-center justify-between rounded-md border border-hairline bg-white">
                              <Button variant="ghost" className="h-11 w-12 rounded-r-none" onClick={own(() => onUpdateQuantity(index, -1))} aria-label="One fewer" data-testid={`decrease-qty-${index}`}><Minus size={16} /></Button>
                              <span className="text-[15px] font-semibold tabular-nums">{item.quantity}</span>
                              <Button variant="ghost" className="h-11 w-12 rounded-l-none" onClick={own(() => onUpdateQuantity(index, 1))} aria-label="One more" data-testid={`increase-qty-${index}`}><Plus size={16} /></Button>
                            </div>
                            <Button variant="outline" className="h-11 gap-1.5 text-signal-stop hover:bg-signal-stop-tint hover:text-signal-stop"
                                    onClick={own(() => { setOpenLine(null); onRemoveItem(index); })} data-testid={`remove-item-${index}`}
                                    title={isNew ? 'Remove' : 'Void (needs a manager)'}>
                              <Trash2 size={15} />{isNew ? 'Remove' : 'Void'}
                            </Button>
                            {adjustable && (
                              <>
                                <Button variant="outline" className="h-11" onClick={own(() => onManagerAction('comp', item))} data-testid={`comp-${index}`}>Comp</Button>
                                <Button variant="outline" className="h-11" onClick={own(() => onManagerAction('price', item))} data-testid={`override-${index}`}>Set price</Button>
                              </>
                            )}
                            <Button variant="outline" className={`h-11 gap-1.5 ${adjustable ? '' : 'col-span-3'}`} onClick={own(() => onEditItem(index))} title="Choices or notes" data-testid={`edit-item-${index}`}>
                              <Edit3 size={14} />{adjustable ? 'Edit' : 'Choices and notes'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Totals + the one verb */}
      <div className="border-t p-3 space-y-2 shrink-0 bg-surface-2">
        <div className="space-y-1 text-[13px]">
          {/* Prices include VAT, so with nothing taken off the subtotal is the total: shown
              only when an offer or a discount makes them differ (Chris's review M7). */}
          {(offersGiven > 0 || hasDiscount) && (
            <div className="flex justify-between text-ink-soft"><span>Subtotal</span><span className="tabular-nums">{formatCurrency(offersGiven > 0 ? itemsGross : grossShown)}</span></div>
          )}
          {offersGiven > 0 && (
            <div className="flex justify-between text-signal-ok" data-testid="offers-total">
              <span className="flex items-center gap-1.5"><Tag size={12} />Offers</span>
              <span className="tabular-nums">-{formatCurrency(offersGiven)}</span>
            </div>
          )}
          {hasDiscount && (
            <div className="flex justify-between items-center text-signal-ok">
              <span className="flex items-center gap-1.5"><Tag size={12} />{discountLabel}
                {canAdjust && onUndoAdjustment && (
                  <button type="button" onClick={() => onUndoAdjustment(isOffer ? 'offer' : 'discount')} className="text-ink-faint hover:text-ink-soft" title={isOffer ? 'Remove offer' : 'Remove discount'} data-testid="remove-discount-btn"><Undo2 size={12} /></button>
                )}
              </span>
              <span className="tabular-nums">-{formatCurrency(discountAmount)}</span>
            </div>
          )}
          {vatShown != null && (
            <div className="flex justify-between text-ink-faint text-[12px]" data-testid="pad-vat"><span>Includes VAT</span><span className="tabular-nums">{formatCurrency(vatShown)}</span></div>
          )}
          {orderPayments.length > 0 && (
            <div className="flex justify-between text-signal-ok text-[12px]">
              <span>Paid so far</span>
              <span className="tabular-nums">-{formatCurrency(orderPayments.reduce((s, p) => s + (p.amount || 0), 0))}</span>
            </div>
          )}
          {notSent > 0 && (
            <div className="flex justify-between font-semibold text-signal-warn" data-testid="not-sent-total">
              <span>Not sent yet</span>
              <span className="tabular-nums">{formatCurrency(notSent)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold pt-1.5 border-t">
            <span>{orderPayments.length > 0 ? 'Outstanding' : 'Total'}</span>
            <span className={`tabular-nums ${orderPayments.length > 0 && remainingBalance > 0 ? 'text-signal-warn' : ''}`} data-testid="pad-total">
              {formatCurrency((orderPayments.length > 0 ? remainingBalance : totalShown) + notSent)}
            </span>
          </div>
        </div>

        {readOnly ? (
          <div className="flex min-h-12 items-center gap-2 border border-hairline bg-white px-3 py-2 text-[14px] font-medium text-ink-soft" data-testid="order-settled">
            <Signal tone={order?.status === 'paid' ? 'ok' : 'stop'}>{String(order?.status || 'closed').replace('_', ' ')}</Signal>
            {order?.paid_at && <span className="font-mono text-[12px] text-ink-faint">{new Date(order.paid_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
            {onRefund && order?.status === 'paid' && (
              // Muted: a refund is a manager's occasional job, not the next thing to do with
              // a paid bill. Still a full-height button, and its own dialog asks for a PIN.
              <Button variant="outline" size="sm" className="ml-auto h-9 border-hairline px-3.5 text-ink-soft hover:text-ink" onClick={onRefund} data-testid="refund-btn">
                <Undo2 size={14} className="mr-1.5" />Refund
              </Button>
            )}
          </div>
        ) : (
        <div className="flex gap-2">
          <Button
            className="flex-1 h-11 text-[15px] bg-verlo hover:bg-verlo-hover"
            onClick={runPrimary}
            disabled={processing || verb.key === 'none'}
            data-testid={mode === 'bar' ? 'take-payment-btn' : verb.key === 'send' ? 'send-to-kitchen-btn' : verb.key === 'bill' ? 'checkout-btn' : 'primary-verb-btn'}
          >
            {processing ? (mode === 'bar' ? 'Taking payment…' : 'Sending…') : verb.key === 'bill' || verb.key === 'pay' ? (
              <><CreditCard size={16} className="mr-2" />{verb.label} · {formatCurrency(verb.amount)}</>
            ) : verb.key === 'close' ? (
              <><DoorOpen size={16} className="mr-2" />{verb.label}</>
            ) : verb.label}
          </Button>
          {(canAdjust || onMessage) && (
            <div className="relative">
              <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => setMoreOpen((v) => !v)} title="More" data-testid="order-more-btn">
                <MoreHorizontal size={16} />
              </Button>
              {moreOpen && (
                <div className="absolute right-0 bottom-full mb-1 z-40 w-48 rounded-lg border bg-white shadow-lg p-1" data-testid="order-more-menu">
                  {onMessage && (
                    <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-paper" onClick={() => { setMoreOpen(false); onMessage(); }} data-testid="add-message-btn">Message the kitchen</button>
                  )}
                  {canAdjust && !hasDiscount && offersAvailable > 0 && onOffers && (
                    <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-paper flex items-center justify-between" onClick={() => { setMoreOpen(false); onOffers(); }} data-testid="offers-btn">
                      <span>Offers</span><span className="text-[11px] text-ink-faint">{offersAvailable} running</span>
                    </button>
                  )}
                  {canAdjust && !hasDiscount && onManagerAction && (
                    <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-paper" onClick={() => { setMoreOpen(false); onManagerAction('discount'); }} data-testid="add-discount-btn">Add a discount</button>
                  )}
                  {newCount > 0 && order?.id && (
                    <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-paper" onClick={() => { setMoreOpen(false); onCheckout(); }}>Take payment for what's sent</button>
                  )}
                  {orderPayments.length > 0 && remainingBalance > 0 && (
                    <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-paper" onClick={() => { setMoreOpen(false); onCheckout(); }}>Take payment</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </Card>
  );
};

export default OrderPanel;
